import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, LogOut, Users, ShieldAlert, Activity, FileText, CalendarDays, Baby, Archive, ArchiveRestore, EyeOff, Menu, X, ClipboardList, Contact, ReceiptText, Stethoscope, ListTodo, Send, Navigation, Megaphone, PhoneCall, Swords } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AddPatientDialog from "@/components/AddPatientDialog";
import StaffManagement from "@/components/StaffManagement";
import NotificationBell from "@/components/NotificationBell";
import CallCapture from "@/components/CallCapture";
import EventsDialog from "@/components/EventsDialog";
import PatientGrid from "@/components/PatientGrid";

const LOGO = "https://content.webfactorysite.co.uk/20260113092111-Wales-Event-Logo.png?t=1768296071";

const londonDay = (iso) =>
  iso ? new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(new Date(iso)) : "";
const todayLondon = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(new Date());

const NAV_BTN = "inline-flex items-center gap-2 text-sm font-semibold px-4 rounded-lg border border-slate-300 text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors min-h-[44px]";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [allPatients, setAllPatients] = useState([]);
  const [stats, setStats] = useState({ treated: 0, active: 0, discharged: 0, high: 0 });
  const [lostStats, setLostStats] = useState({ total: 0, with_us: 0, reunited: 0 });
  const [pastEvents, setPastEvents] = useState([]);
  const [archivedPatients, setArchivedPatients] = useState([]);

  async function archiveEvent(ev, archived) {
    try {
      await api.patch(`/events/${ev.id}`, { archived });
      toast.success(archived ? `"${ev.name}" moved to Past events — patients are hidden, not deleted` : `"${ev.name}" restored to current events`);
      loadStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function hideEvent(ev) {
    try {
      await api.patch(`/events/${ev.id}`, { hidden: true });
      toast.success(`"${ev.name}" hidden — bring it back from the Calendar if you need it`);
      loadStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function archivePatient(p, archived) {
    try {
      const { data } = await api.patch(`/patients/${p.id}/archive`, { archived });
      toast.success(data.message);
      loadStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  const [q, setQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");
  const [sharing, setSharing] = useState(false);
  const [locationEnabled, setLocationEnabled] = useState(true);
  const [fightCardsToday, setFightCardsToday] = useState([]);
  const [lastSent, setLastSent] = useState(null);
  const watchId = useRef(null);
  const lastPost = useRef(0);

  const postLoc = useCallback(async (pos) => {
    const nowMs = Date.now();
    if (nowMs - lastPost.current < 20000) return;
    lastPost.current = nowMs;
    try {
      await api.post("/staff/location", {
        lat: pos.coords.latitude, lng: pos.coords.longitude,
        accuracy: Math.round(pos.coords.accuracy || 0),
      });
      setLastSent(new Date());
    } catch (e) {
      if (e?.response?.status === 403) {
        setLocationEnabled(false);
        if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
        watchId.current = null;
        setSharing(false);
      } else {
        console.debug("Location post failed; will retry on next update:", e?.message || e);
      }
    }
  }, []);

  const startSharing = () => {
    if (!locationEnabled) {
      toast.error("Phone location sharing is switched off by control");
      return;
    }
    if (!navigator.geolocation) {
      toast.error("This device has no GPS/location");
      return;
    }
    watchId.current = navigator.geolocation.watchPosition(postLoc, (err) => {
      toast.error(err.code === 1 ? "Location permission denied — allow it in your browser settings" : "Couldn't get your location");
      setSharing(false);
    }, { enableHighAccuracy: true, maximumAge: 15000 });
    setSharing(true);
  };

  const stopSharing = async () => {
    if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
    watchId.current = null;
    setSharing(false);
    setLastSent(null);
    await api.post("/staff/location/stop").catch((e) => console.debug("Location stop request failed:", e?.message || e));
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/staff/location/settings")
      .then(({ data }) => { if (!cancelled) setLocationEnabled(Boolean(data.enabled)); })
      .catch((e) => console.debug("Location settings request failed:", e?.message || e));
    api.get("/my/fight-card-today")
      .then(({ data }) => { if (!cancelled) setFightCardsToday(data.events || []); })
      .catch((e) => console.debug("Fight card check failed:", e?.message || e));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => {
    if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
  }, []);
  const [dayFilter, setDayFilter] = useState("");
  const [eventMeta, setEventMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (filter === "discharged") params.status = "discharged";
      else if (filter !== "all") params.triage = filter;
      if (eventFilter !== "all") params.event = eventFilter;
      if (dayFilter) params.date = dayFilter;
      const { data } = await api.get("/patients", { params });
      setPatients(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, filter, eventFilter, dayFilter]);

  const loadStats = useCallback(async () => {
    try {
      const [pRes, eRes, lRes, aRes] = await Promise.all([
        api.get("/patients"), api.get("/events"), api.get("/lost-children"),
        api.get("/patients", { params: { include_archived: "true" } }),
      ]);
      const data = pRes.data;
      setAllPatients(data);
      setPastEvents(eRes.data.filter((e) => e.id && e.archived && !e.hidden));
      setArchivedPatients(aRes.data.filter((p) => p.archived));
      const londonDay = (dt) => (dt ? new Date(dt).toLocaleDateString("en-CA", { timeZone: "Europe/London" }) : "");
      const todayStr = londonDay(new Date());
      const lostDay = (c) => c.occurred_date || londonDay(c.created_at);
      const todayLost = lRes.data.filter((c) => lostDay(c) === todayStr);
      setLostStats({
        total: todayLost.length,
        with_us: lRes.data.filter((c) => c.status === "with_us").length,
        reunited: todayLost.filter((c) => c.status === "reunited").length,
      });
      const today = todayLondon();
      const seenToday = data.filter((p) => londonDay(p.arrived_at || p.created_at) === today);
      setStats({
        treated: seenToday.length,
        active: data.filter((p) => p.status === "active").length,
        discharged: seenToday.filter((p) => p.status === "discharged").length,
        high: data.filter((p) => p.triage === "high" && p.status === "active").length,
      });
      const meta = {};
      eRes.data.forEach((e) => { meta[e.name] = e; });
      setEventMeta(meta);
    } catch (e) {
      console.error("Failed to load dashboard stats:", e?.message || e);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const events = {};
  allPatients.forEach((p) => {
    const name = p.event_location?.trim() || "Unassigned";
    if (!events[name]) events[name] = { treated: 0, active: 0, discharged: 0, high: 0 };
    const s = events[name];
    s.treated += 1;
    if (p.status === "discharged") s.discharged += 1;
    else {
      s.active += 1;
      if (p.triage === "high") s.high += 1;
    }
  });
  const eventNames = Object.keys(events).sort((a, b) => (a === "Unassigned" ? 1 : b === "Unassigned" ? -1 : a.localeCompare(b)));
  const groupMap = {};
  eventNames.forEach((name) => {
    let label, sortKey;
    if (name === "Unassigned") {
      label = "Unassigned";
      sortKey = "9999-99";
    } else {
      const date = eventMeta[name]?.event_date;
      if (date) {
        const d = new Date(`${date}T00:00:00`);
        label = `${d.toLocaleDateString("en-GB", { weekday: "long" })} night's events`;
        sortKey = date;
      } else {
        label = "Undated events";
        sortKey = "9999-98";
      }
    }
    if (!groupMap[label]) groupMap[label] = { sortKey, names: [] };
    groupMap[label].names.push(name);
  });
  const eventGroups = Object.entries(groupMap).sort((a, b) => a[1].sortKey.localeCompare(b[1].sortKey));

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="staff-dashboard">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 sm:px-8 py-3 flex justify-between items-center shadow-sm">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="WEMS" className="h-9 w-auto" />
          <div className="hidden sm:block">
            <p className="font-heading font-bold text-[#0F2547] text-sm leading-tight">Staff Portal</p>
            <p className="text-[11px] text-slate-500">Patient & Observations</p>
          </div>
        </div>
        <div className="md:hidden flex items-center gap-2">
          {user?.role === "admin" && <NotificationBell />}
          <button onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu"
            className="h-11 w-11 grid place-items-center rounded-lg border border-slate-300 text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors">
            <Menu className="h-5 w-5" />
          </button>
        </div>
        <div className="hidden md:flex items-center gap-2">
          <Link to="/calendar" data-testid="calendar-link" className={NAV_BTN}>
            <CalendarDays className="h-4 w-4" /> <span className="hidden lg:inline">Calendar</span>
          </Link>
          <Link to="/lost-children" data-testid="lost-children-link" className={NAV_BTN}>
            <Baby className="h-4 w-4" /> <span className="hidden lg:inline">Lost Children</span>
          </Link>
          {locationEnabled && (
            <button onClick={sharing ? stopSharing : startSharing} data-testid="location-share-toggle"
              className={`inline-flex items-center gap-2 text-sm font-semibold px-4 rounded-lg transition-colors min-h-[44px] ${sharing ? "bg-[#007F3B] text-white hover:bg-[#006631]" : "border border-slate-300 text-[#0F2547] hover:border-[#007F3B] hover:text-[#007F3B]"}`}>
              <Navigation className="h-4 w-4" /> <span className="hidden lg:inline">{sharing ? "Sharing — Stop" : "Share location"}</span>
            </button>
          )}
          {fightCardsToday.map((fc) => (
            <Link key={fc.id} to={`/events/${fc.id}/fight-card`} data-testid={`fight-card-shortcut-${fc.id}`}
              className="inline-flex items-center gap-2 text-sm font-bold px-4 rounded-lg bg-[#0F2547] text-white hover:bg-[#173766] transition-colors min-h-[44px]">
              <Swords className="h-4 w-4" /> <span className="hidden lg:inline">Fight card</span>
            </Link>
          ))}
          {user?.role === "admin" && (
            <button onClick={() => setCallOpen(true)} data-testid="dash-on-the-phone"
              className="inline-flex items-center gap-2 text-sm font-bold px-4 rounded-lg bg-[#007F3B] text-white hover:bg-[#006631] transition-colors min-h-[44px]">
              <PhoneCall className="h-4 w-4" /> <span className="hidden lg:inline">On the phone</span>
            </button>
          )}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="admin-hub-link"
              className="inline-flex items-center gap-2 text-sm font-bold px-4 rounded-lg bg-[#005EB8] text-white hover:bg-[#004a93] transition-colors min-h-[44px]">
              Admin
            </Link>
          )}
          {user?.role === "admin" && <NotificationBell />}
          <div className="hidden lg:flex flex-col justify-center items-end self-center shrink-0 leading-tight mr-1">
            <span className="text-sm font-semibold text-[#0F2547] whitespace-nowrap" data-testid="current-user-name">{user?.name}</span>
            <span className="text-[11px] font-bold uppercase tracking-wide text-[#005EB8]" data-testid="current-user-role">{user?.role_label}</span>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/staff/login"); }}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 rounded-lg bg-[#0F2547] text-white hover:bg-[#173766] transition-colors min-h-[44px]"
          >
            <LogOut className="h-4 w-4" /> <span className="hidden lg:inline">Sign out</span>
          </button>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <img src={LOGO} alt="WEMS" className="h-8 w-auto" />
              <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu"
                className="h-10 w-10 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-sm font-semibold text-[#0F2547]">{user?.name}</p>
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#005EB8]">{user?.role_label}</p>
            </div>
            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
              <Link to="/calendar" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-calendar"
                className="flex items-center gap-3 px-3 py-3.5 rounded-lg text-sm font-semibold text-[#0F2547] hover:bg-sky-50 hover:text-[#005EB8] transition-colors">
                <CalendarDays className="h-5 w-5" /> Calendar
              </Link>
              <Link to="/lost-children" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-lost"
                className="flex items-center gap-3 px-3 py-3.5 rounded-lg text-sm font-semibold text-[#0F2547] hover:bg-sky-50 hover:text-[#005EB8] transition-colors">
                <Baby className="h-5 w-5" /> Lost Children
              </Link>
              {locationEnabled && (
                <button onClick={() => { sharing ? stopSharing() : startSharing(); }} data-testid="mobile-nav-share-location"
                  className={`w-full flex items-center gap-3 px-3 py-3.5 rounded-lg text-sm font-semibold transition-colors ${sharing ? "text-[#007F3B] bg-emerald-50" : "text-[#0F2547] hover:bg-sky-50 hover:text-[#005EB8]"}`}>
                  <Navigation className="h-5 w-5" /> {sharing ? "Sharing location — tap to stop" : "Share my location with control"}
                </button>
              )}
              {fightCardsToday.map((fc) => (
                <Link key={fc.id} to={`/events/${fc.id}/fight-card`} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-fight-card-${fc.id}`}
                  className="w-full flex items-center gap-3 px-3 py-3.5 rounded-lg text-sm font-bold text-white bg-[#0F2547] hover:bg-[#173766] transition-colors">
                  <Swords className="h-5 w-5" /> Fight card — {fc.name}
                </Link>
              ))}
              {user?.role === "admin" && (
                <div>
                  <Link to="/admin" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-admin"
                    className="flex items-center gap-3 px-3 py-3.5 rounded-lg text-sm font-bold text-white bg-[#005EB8] hover:bg-[#004a93] transition-colors">
                    <ShieldAlert className="h-5 w-5" /> Admin
                  </Link>
                  <div className="ml-4 mt-1 pl-3 border-l-2 border-slate-200 space-y-1">
                    <Link to="/admin/staff" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-rota"
                      className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-[#0F2547] hover:bg-sky-50 hover:text-[#005EB8] transition-colors">
                      <ClipboardList className="h-5 w-5" /> Staff
                    </Link>
                    <Link to="/admin/contacts" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-contacts"
                      className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-[#0F2547] hover:bg-sky-50 hover:text-[#005EB8] transition-colors">
                      <Contact className="h-5 w-5" /> Client contacts
                    </Link>
                    <Link to="/admin/invoices" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-invoices"
                      className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-[#0F2547] hover:bg-sky-50 hover:text-[#005EB8] transition-colors">
                      <ReceiptText className="h-5 w-5" /> Quotes & invoices
                    </Link>
                    <Link to="/admin/medical-plan" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-medical-plan"
                      className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-[#0F2547] hover:bg-sky-50 hover:text-[#005EB8] transition-colors">
                      <Stethoscope className="h-5 w-5" /> Medical Plan
                    </Link>
                    <Link to="/admin/marketing" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-marketing"
                      className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-[#0F2547] hover:bg-sky-50 hover:text-[#005EB8] transition-colors">
                      <Megaphone className="h-5 w-5" /> Marketing
                    </Link>
                    <Link to="/admin/jobs" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-jobs"
                      className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-[#0F2547] hover:bg-sky-50 hover:text-[#005EB8] transition-colors">
                      <ListTodo className="h-5 w-5" /> To-Do
                    </Link>
                    <Link to="/admin/broadcast" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-broadcast"
                      className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-[#0F2547] hover:bg-sky-50 hover:text-[#005EB8] transition-colors">
                      <Send className="h-5 w-5" /> Send shift to staff
                    </Link>
                  </div>
                </div>
              )}
            </nav>
            <div className="p-3 border-t border-slate-200">
              <button data-testid="mobile-nav-logout"
                onClick={async () => { setMenuOpen(false); await logout(); navigate("/staff/login"); }}
                className="w-full flex items-center gap-3 px-3 py-3.5 rounded-lg text-sm font-semibold text-white bg-[#0F2547] hover:bg-[#173766] transition-colors">
                <LogOut className="h-5 w-5" /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto p-4 sm:p-8">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="control-room-stats">
          <Link to="/lost-children" data-testid="stat-lost-children-card"
            className="bg-white rounded-xl border border-slate-200 p-5 hover:border-[#005EB8] hover:shadow-md transition-all block">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1"><Baby className="h-3.5 w-3.5 text-[#005EB8]" /> Lost children today</p>
            <p className="font-vitals text-3xl font-extrabold text-[#0F2547]" data-testid="stat-lost-children">{lostStats.total}</p>
            <p className="text-[11px] font-semibold text-slate-400 mt-1">
              <span className="text-[#005EB8]">{lostStats.with_us} with us now</span> · <span className="text-emerald-600">{lostStats.reunited} reunited today</span>
            </p>
          </Link>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1"><Activity className="h-3.5 w-3.5 text-[#005EB8]" /> With you now</p>
            <p className="font-vitals text-3xl font-extrabold text-[#005EB8]" data-testid="stat-active-patients">{stats.active}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5 text-[#D5281B]" /> High priority</p>
            <p className="font-vitals text-3xl font-extrabold text-[#D5281B]" data-testid="stat-high-priority">{stats.high}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1"><Users className="h-3.5 w-3.5 text-slate-400" /> Discharged today</p>
            <p className="font-vitals text-3xl font-extrabold text-slate-600" data-testid="stat-discharged-patients">{stats.discharged}</p>
          </div>
          <div className="bg-[#0F2547] rounded-xl p-5 text-white">
            <p className="text-xs font-semibold uppercase tracking-wider text-sky-300 mb-1">Treated today</p>
            <p className="font-vitals text-3xl font-extrabold" data-testid="stat-total-patients">{stats.treated}</p>
          </div>
        </div>

        {eventNames.length > 0 && (
          <div className="mb-8" data-testid="event-breakdown">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-bold text-[#0F2547]">Breakdown by event</h2>
              <div className="flex items-center gap-3">
                <button onClick={() => setEventsOpen(true)} data-testid="manage-events-btn"
                  className="text-xs font-semibold text-[#0F2547] border border-slate-300 rounded-lg px-3 py-2 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors min-h-[36px]">
                  Manage events
                </button>
                {eventFilter !== "all" && (
                  <button onClick={() => setEventFilter("all")} data-testid="clear-event-filter"
                    className="text-xs font-semibold text-[#005EB8] hover:underline min-h-[36px]">
                    Show all events
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-5">
              {eventGroups.map(([label, group]) => (
                <div key={label} data-testid={`event-group-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{label}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {group.names.map((name) => {
                      const i = eventNames.indexOf(name);
                      const s = events[name];
                      const selected = eventFilter === (name === "Unassigned" ? "__none__" : name);
                      const meta = eventMeta[name];
                      return (
                        <button
                          key={name}
                          data-testid={`event-card-${i}`}
                          onClick={() => setEventFilter(selected ? "all" : name === "Unassigned" ? "__none__" : name)}
                          className={`text-left rounded-xl border p-4 transition-all bg-white hover:border-[#005EB8] hover:shadow-md ${selected ? "border-[#005EB8] ring-2 ring-[#005EB8]/30 shadow-md" : "border-slate-200"}`}
                        >
                          <p className="font-heading font-bold text-[#0F2547] text-sm leading-tight truncate">{name}</p>
                          {(meta?.event_date || meta?.venue) && (
                            <p className="text-[11px] text-slate-400 mt-0.5 mb-1 truncate">
                              {meta.event_date && new Date(`${meta.event_date}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                              {meta.event_date && meta.venue && " · "}
                              {meta.venue}
                            </p>
                          )}
                          <div className="flex items-center gap-3 text-[11px] font-bold mt-2">
                            <span className="text-[#005EB8]">{s.active} active</span>
                            <span className={s.high > 0 ? "text-[#D5281B]" : "text-slate-400"}>{s.high} high</span>
                            <span className="text-slate-500">{s.discharged} disch.</span>
                            <span className="ml-auto text-slate-400 font-vitals">{s.treated} total</span>
                            {user?.role === "admin" && meta?.id && (
                              <span
                                data-testid={`event-archive-btn-${i}`}
                                title={`Archive ${name} — moves it and its patients to Past events (nothing is deleted)`}
                                onClick={(e) => { e.stopPropagation(); archiveEvent(meta, true); }}
                                className="text-slate-400 hover:text-amber-600 transition-colors p-1 cursor-pointer"
                              >
                                <Archive className="h-4 w-4" />
                              </span>
                            )}
                            <span
                              data-testid={`event-report-btn-${i}`}
                              title={`End of Event report for ${name}`}
                              onClick={(e) => { e.stopPropagation(); navigate(`/reports/organizer?event=${encodeURIComponent(name === "Unassigned" ? "__none__" : name)}`); }}
                              className="text-slate-400 hover:text-[#005EB8] transition-colors p-1 cursor-pointer"
                            >
                              <FileText className="h-4 w-4" />
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {pastEvents.length > 0 && (
          <div className="mb-8" data-testid="past-events-section">
            <h2 className="font-heading text-lg font-bold text-slate-500 mb-3 flex items-center gap-2">
              <Archive className="h-4 w-4" /> Past events ({pastEvents.length})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pastEvents.map((ev, i) => (
                <div key={ev.id} data-testid={`past-event-card-${i}`}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="font-heading font-bold text-slate-500 text-sm leading-tight truncate">{ev.name}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5 mb-2 truncate">
                    {ev.event_date && new Date(`${ev.event_date}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                    {ev.event_date && ev.venue && " · "}{ev.venue}
                  </p>
                  <p className="text-[11px] font-semibold text-slate-400 mb-3">
                    {ev.patient_count ?? 0} patients · {ev.lost_count ?? 0} lost children
                  </p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => archiveEvent(ev, false)} data-testid={`event-unarchive-btn-${i}`}
                      className="text-xs font-bold text-[#005EB8] hover:bg-sky-100 rounded-lg px-3 py-2 transition-colors min-h-[36px] inline-flex items-center gap-1.5">
                      <ArchiveRestore className="h-3.5 w-3.5" /> Restore
                    </button>
                    <button onClick={() => navigate(`/reports/organizer?event=${encodeURIComponent(ev.name)}`)}
                      data-testid={`past-event-report-btn-${i}`}
                      className="text-xs font-bold text-slate-400 hover:text-[#005EB8] hover:bg-sky-50 rounded-lg px-3 py-2 transition-colors min-h-[36px] inline-flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5" /> Report
                    </button>
                    <button onClick={() => hideEvent(ev)} data-testid={`past-event-hide-btn-${i}`}
                      title="Hide from this list — bring it back from the Calendar"
                      className="text-xs font-bold text-slate-400 hover:text-[#D5281B] hover:bg-red-50 rounded-lg px-3 py-2 transition-colors min-h-[36px] inline-flex items-center gap-1.5 ml-auto">
                      <EyeOff className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="patient-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, phone, event, NHS number…"
              className="w-full h-12 rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
            />
          </div>
          <select
            data-testid="patient-triage-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-12 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
          >
            <option value="all">All patients</option>
            <option value="high">High priority</option>
            <option value="medium">Monitor</option>
            <option value="low">Stable</option>
            <option value="discharged">Discharged</option>
          </select>
          <input
            type="date"
            value={dayFilter}
            onChange={(e) => setDayFilter(e.target.value)}
            data-testid="patient-date-filter"
            title="Filter by day seen — leave empty for all dates"
            className="h-12 rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
          />
          {dayFilter && (
            <button
              onClick={() => setDayFilter("")}
              data-testid="patient-date-clear"
              className="h-12 px-3 rounded-lg border border-slate-300 text-xs font-bold text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors"
            >
              All dates
            </button>
          )}
          <button
            data-testid="add-patient-button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white font-semibold text-sm"
          >
            <Plus className="h-4 w-4" /> Add Patient
          </button>
        </div>

        <PatientGrid patients={patients} loading={loading} />
      </main>

      <AddPatientDialog open={addOpen} onOpenChange={setAddOpen} onSaved={() => { setAddOpen(false); load(); loadStats(); }} />
      {user?.role === "admin" && <StaffManagement open={staffOpen} onOpenChange={setStaffOpen} />}
      {user?.role === "admin" && <CallCapture open={callOpen} onOpenChange={setCallOpen} />}
      {user?.role === "admin" && archivedPatients.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-8 pb-8" data-testid="archived-patients-section">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="font-heading text-lg font-bold text-[#0F2547] mb-1 flex items-center gap-2">
              <Archive className="h-4 w-4 text-slate-400" /> Archived patients ({archivedPatients.length})
            </h2>
            <p className="text-xs text-slate-400 mb-3">Hidden from the dashboard — records are kept and still appear in reports.</p>
            <div className="space-y-2">
              {archivedPatients.map((p, i) => (
                <div key={p.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5" data-testid={`archived-patient-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0F2547] truncate">{p.first_name} {p.last_name}</p>
                    <p className="text-[11px] text-slate-400 truncate">{p.event_location || "Unassigned"}{p.status === "discharged" ? " · Discharged" : ""}</p>
                  </div>
                  <button onClick={() => archivePatient(p, false)} data-testid={`patient-restore-btn-${i}`}
                    className="text-xs font-bold text-[#005EB8] hover:bg-sky-50 rounded-lg px-3 py-2 transition-colors min-h-[36px] inline-flex items-center gap-1.5">
                    <ArchiveRestore className="h-3.5 w-3.5" /> Restore
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <EventsDialog open={eventsOpen} onOpenChange={setEventsOpen} onChanged={() => loadStats()} />
    </div>
  );
}
